import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import axios from "axios";
import { assignFundSource } from "../assignFundSource";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import Loader from "../../../components/atoms/Loader";
import styles from "../index.module.css";

function EligibilityCollectEmail() {
  const navigate = useNavigate();

  const userData = useEligibilityData();

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [gsiLoaded, setGsiLoaded] = useState(false);

  // Set when email already exists in DB — triggers confirm or Gmail verify UI
  const [savedEmail, setSavedEmail] = useState("");
  const [savedStage, setSavedStage] = useState<number | null>(null);

  // Gmail-specific
  const [gmailMismatch, setGmailMismatch] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Non-Gmail confirm
  const [confirming, setConfirming] = useState(false);

  const googleBtnRef = useRef<HTMLDivElement>(null);
  const verifyBtnRef = useRef<HTMLDivElement>(null);
  const onGoogleEmailRef = useRef<(e: string) => void>(() => {});

  useEffect(() => {
    if (userData?.userId && userData?.applicationId) fetchSummary();
    if ((window as any).google?.accounts?.id) {
      setGsiLoaded(true);
    } else {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.onload = () => setGsiLoaded(true);
      document.head.appendChild(script);
    }
  }, []); // eslint-disable-line

  const isValidEmail = (val: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const isGmail = (val: string) => val.toLowerCase().endsWith("@gmail.com");

  const navigateByStage = (stage: number, emailVal: string) => {
    if (stage === 5) { navigate("/eligibility/under-review", { replace: true, state: { data: userData } }); return; }
    if (stage === 8) { navigate("/eligibility/congratulations", { replace: true, state: { data: userData } }); return; }
    if (stage === 9) { navigate("/eligibility/selfie", { replace: true, state: { data: userData } }); return; }
    if (stage === 11) { navigate("/eligibility/agreement", { replace: true, state: { data: userData } }); return; }
    if (stage === 12) { navigate("/eligibility/emandate", { replace: true, state: { data: userData } }); return; }
    if (stage === 24) { navigate("/eligibility/digilocker", { replace: true, state: { data: userData } }); return; }
    if (stage === 25) { navigate("/eligibility/evaluating", { replace: true, state: { data: userData } }); return; }
    if (stage === 18) { navigate("/eligibility/rejected", { replace: true, state: { data: userData } }); return; }
    if (stage === 10) {
      (async () => {
        try {
          const token = sessionStorage.getItem("eligibility_auth_token");
          const res = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/summary/user?id=${userData.userId}&applicationId=${userData.applicationId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const result = await res.json();
          const d = result?.data;
          const rawInstId = d?.instituteId;
          const instId = Array.isArray(rawInstId) ? rawInstId[0]?.id : rawInstId;
          const courseComplete = instId && d?.courseName?.trim() && d?.courseFees && d?.loanTenure;
          if (!courseComplete) {
            navigate("/eligibility/course-details", { replace: true, state: { data: userData, fromStage10: true } });
          } else {
            assignFundSource(userData, sessionStorage.getItem("eligibility_auth_token"));
            navigate("/eligibility/evaluating", { replace: true, state: { data: userData, email: emailVal } });
          }
        } catch {
          assignFundSource(userData, sessionStorage.getItem("eligibility_auth_token"));
          navigate("/eligibility/evaluating", { replace: true, state: { data: userData, email: emailVal } });
        }
      })();
      return;
    }
    assignFundSource(userData, sessionStorage.getItem("eligibility_auth_token"));
    navigate("/eligibility/evaluating", { replace: true, state: { data: userData, email: emailVal } });
  };

  const proceedWithSavedEmail = (stageOverride?: number | null) => {
    const stage = stageOverride !== undefined ? stageOverride : savedStage;
    if (stage !== null) {
      navigateByStage(stage, savedEmail);
    } else {
      assignFundSource(userData, sessionStorage.getItem("eligibility_auth_token"));
      navigate("/eligibility/evaluating", { replace: true, state: { data: userData, email: savedEmail } });
    }
  };

  const fetchSummary = async () => {
    try {
      const token = sessionStorage.getItem("eligibility_auth_token");
      const res = await fetch(
        `${process.env.REACT_APP_DASHBOARD_URL}/summary/user?id=${userData.userId}&applicationId=${userData.applicationId}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const result = await res.json();
      const d = result?.data;
      if (d?.email && isValidEmail(d.email)) {
        // Fetch stage for both Gmail and non-Gmail
        let stage: number | null = null;
        try {
          const stageRes = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/stage?applicationId=${userData.applicationId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const stageData = await stageRes.json();
          stage = stageData?.data?.stage ?? null;
        } catch {}
        setSavedStage(stage);
        setSavedEmail(d.email);
        return;
      }
      if (d?.email) setEmail(d.email);
    } catch {}
  };

  const saveEmail = async (emailVal: string) => {
    setError("");
    setLoading(true);
    const token = sessionStorage.getItem("eligibility_auth_token");
    try {
      await axios.post(
        `${process.env.REACT_APP_DASHBOARD_URL}/summary/update`,
        { applicationId: userData.applicationId, userId: userData.userId, email: emailVal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      console.log("summary/update email error:", e);
    }
    try {
      const stageRes = await fetch(
        `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/stage?applicationId=${userData.applicationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const stageData = await stageRes.json();
      navigateByStage(stageData?.data?.stage, emailVal);
    } catch {
      assignFundSource(userData, sessionStorage.getItem("eligibility_auth_token"));
      navigate("/eligibility/evaluating", { replace: true, state: { data: userData, email: emailVal } });
    } finally {
      setLoading(false);
    }
  };

  // Keep ref in sync so the Google callback always calls the latest version
  onGoogleEmailRef.current = (emailVal: string) => {
    if (savedEmail) {
      // Gmail verification mode
      if (emailVal.toLowerCase() === savedEmail.toLowerCase()) {
        setGmailMismatch(false);
        setVerifying(true);
        proceedWithSavedEmail();
      } else {
        setGmailMismatch(true);
      }
    } else {
      // Collection mode: save and proceed
      setEmail(emailVal);
      saveEmail(emailVal);
    }
  };

  // Render Google Sign-In button for collection mode (no saved email)
  useEffect(() => {
    if (!gsiLoaded || !googleBtnRef.current || savedEmail) return;
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;
    const google = (window as any).google;
    google.accounts.id.initialize({
      client_id: clientId,
      callback: (res: any) => {
        try {
          const base64 = res.credential.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
          const payload = JSON.parse(atob(base64));
          if (payload.email) onGoogleEmailRef.current(payload.email);
        } catch {}
      },
    });
    google.accounts.id.renderButton(googleBtnRef.current, {
      theme: "outline",
      size: "large",
      width: googleBtnRef.current.offsetWidth || 280,
      text: "continue_with",
      shape: "pill",
    });
  }, [gsiLoaded, savedEmail]);

  // Render verify button + trigger One Tap for Gmail verification mode
  useEffect(() => {
    if (!gsiLoaded || !verifyBtnRef.current || !savedEmail || !isGmail(savedEmail)) return;
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;
    const google = (window as any).google;
    google.accounts.id.initialize({
      client_id: clientId,
      auto_select: true,
      callback: (res: any) => {
        try {
          const base64 = res.credential.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
          const payload = JSON.parse(atob(base64));
          if (payload.email) onGoogleEmailRef.current(payload.email);
        } catch {}
      },
    });
    google.accounts.id.renderButton(verifyBtnRef.current, {
      theme: "filled_blue",
      size: "large",
      width: verifyBtnRef.current.offsetWidth || 280,
      text: "signin_with",
      shape: "pill",
    });
    google.accounts.id.prompt();
  }, [gsiLoaded, savedEmail]);

  const handleContinue = async () => {
    if (!email.trim()) { setError("Please enter your email address."); return; }
    if (!isValidEmail(email)) { setError("Please enter a valid email address."); return; }
    await saveEmail(email);
  };

  const hasGoogleClientId = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // ── Gmail verification UI ──
  if (savedEmail && isGmail(savedEmail)) {
    return (
      <div className={styles.body}>
        <div className={styles.backdrop}>
          <Navbar />
          <div className={styles.main}>
            <div className={styles.cardWrapper}>
              <div className={styles.container}>

                <div className={styles.cardHeader}>
                  <div style={{ width: "2.4rem", height: "2.4rem", borderRadius: "50%", background: "#F4E5DD", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" fill="#d32028" opacity="0.15" stroke="#d32028" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M22 8L12 14L2 8" stroke="#d32028" strokeWidth="1.5" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <p className={styles.cardTitle}>Verify Email</p>
                </div>

                <p style={{ fontSize: "0.82rem", color: "#666", margin: "0 0 1rem", lineHeight: 1.6 }}>
                  We found an existing email linked to your account. Please sign in with Google to verify it's you.
                </p>

                <div style={{ background: "#f9f9f9", border: "1px solid #e8e8e8", borderRadius: "10px", padding: "0.8rem 1rem", marginBottom: "1.2rem", display: "flex", alignItems: "center", gap: "0.6rem" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#888" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M22 8L12 14L2 8" stroke="#888" strokeWidth="1.5" strokeLinejoin="round" />
                  </svg>
                  <span style={{ fontSize: "0.88rem", color: "#333", fontFamily: "Outfit, sans-serif", fontWeight: 600 }}>{savedEmail}</span>
                </div>

                {hasGoogleClientId && (
                  <div ref={verifyBtnRef} style={{ width: "100%", minHeight: "44px" }} />
                )}

                {verifying && (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "1rem" }}>
                    <Loader />
                    <p style={{ color: "#888", fontSize: "0.82rem", margin: 0, fontFamily: "Outfit, sans-serif" }}>Verifying…</p>
                  </div>
                )}

                {gmailMismatch && (
                  <div style={{ marginTop: "0.85rem", background: "#fdecea", border: "1px solid #f9d8d6", borderRadius: "8px", padding: "0.7rem 0.9rem" }}>
                    <p style={{ color: "#d32028", fontSize: "0.82rem", margin: 0, lineHeight: 1.5 }}>
                      The signed-in Google account doesn't match your registered email. Please sign in with <strong>{savedEmail}</strong>.
                    </p>
                  </div>
                )}

              </div>
              <Footer light />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Non-Gmail confirm UI ──
  if (savedEmail) {
    return (
      <div className={styles.body}>
        <div className={styles.backdrop}>
          <Navbar />
          <div className={styles.main}>
            <div className={styles.cardWrapper}>
              <div className={styles.container}>

                <div className={styles.cardHeader}>
                  <div style={{ width: "2.4rem", height: "2.4rem", borderRadius: "50%", background: "#F4E5DD", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" fill="#d32028" opacity="0.15" stroke="#d32028" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M22 8L12 14L2 8" stroke="#d32028" strokeWidth="1.5" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <p className={styles.cardTitle}>Confirm Email</p>
                </div>

                <p style={{ fontSize: "0.82rem", color: "#666", margin: "0 0 1rem", lineHeight: 1.6 }}>
                  We found an existing email linked to your account. Is this your correct email address?
                </p>

                <div style={{ background: "#f9f9f9", border: "1px solid #e8e8e8", borderRadius: "10px", padding: "0.8rem 1rem", marginBottom: "0.6rem", display: "flex", alignItems: "center", gap: "0.6rem" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#888" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M22 8L12 14L2 8" stroke="#888" strokeWidth="1.5" strokeLinejoin="round" />
                  </svg>
                  <span style={{ fontSize: "0.88rem", color: "#333", fontFamily: "Outfit, sans-serif", fontWeight: 600 }}>{savedEmail}</span>
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 0 1.2rem" }}>
                  <p style={{ fontSize: "0.72rem", color: "#aaa", margin: 0, lineHeight: 1.55 }}>
                    This email will be used for all future communication.
                  </p>
                  <button
                    onClick={() => { setEmail(savedEmail); setSavedEmail(""); setSavedStage(null); }}
                    style={{ background: "none", border: "none", color: "#d32028", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline", whiteSpace: "nowrap", marginLeft: "0.5rem", padding: 0,marginTop:'1px' }}
                  >
                    Change
                  </button>
                </div>

                {confirming && (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                    <Loader />
                    <p style={{ color: "#888", fontSize: "0.82rem", margin: 0, fontFamily: "Outfit, sans-serif" }}>Please wait…</p>
                  </div>
                )}

              </div>

              <div style={{ padding: "0rem 1.25rem 1rem", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "0.6rem" }}>
                <button
                  onClick={() => { setConfirming(true); proceedWithSavedEmail(); }}
                  disabled={confirming}
                  style={{
                    width: "55%",
                    padding: "0.8rem 1.5rem",
                    background: confirming ? "#aaa" : "#d32028",
                    color: "#fff",
                    border: "none",
                    borderRadius: "60px",
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    fontFamily: "Outfit, sans-serif",
                    cursor: confirming ? "not-allowed" : "pointer",
                    letterSpacing: "0.3px",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                  }}
                >
                  Yes, Confirm
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              <Footer light />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Normal email collection UI ──
  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>

              <div className={styles.cardHeader}>
                <div
                  style={{
                    width: "2.4rem",
                    height: "2.4rem",
                    borderRadius: "50%",
                    background: "#F4E5DD",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" fill="#d32028" opacity="0.15" stroke="#d32028" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M22 8L12 14L2 8" stroke="#d32028" strokeWidth="1.5" strokeLinejoin="round" />
                  </svg>
                </div>
                <p className={styles.cardTitle}>Email Address</p>
              </div>

              {hasGoogleClientId && (
                <>
                  <p style={{ fontSize: "0.78rem", color: "#888", margin: "0 0 0.6rem", lineHeight: 1.5 }}>
                    Sign in with Google to auto-fill your email
                  </p>
                  <div ref={googleBtnRef} style={{ width: "100%", minHeight: "44px", marginBottom: "0.8rem" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", margin: "0 0 0.85rem" }}>
                    <div style={{ flex: 1, height: "1px", background: "#ebebeb" }} />
                    <span style={{ fontSize: "0.72rem", color: "#bbb", fontWeight: 500, whiteSpace: "nowrap" }}>or enter manually</span>
                    <div style={{ flex: 1, height: "1px", background: "#ebebeb" }} />
                  </div>
                </>
              )}

              <div>
                <span style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.08em", color: "#444", textTransform: "uppercase", marginBottom: "0.4rem", display: "block" }}>
                  Email Address
                </span>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", pointerEvents: "none" }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#aaa" strokeWidth="1.6" strokeLinejoin="round" />
                      <path d="M22 8L12 14L2 8" stroke="#aaa" strokeWidth="1.6" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleContinue()}
                    style={{
                      width: "100%",
                      padding: "0.72rem 0.9rem 0.72rem 2.4rem",
                      border: `1px solid ${error ? "#d32028" : "#e0e0e0"}`,
                      borderRadius: "8px",
                      fontSize: "0.88rem",
                      outline: "none",
                      boxSizing: "border-box",
                      fontFamily: "Outfit, sans-serif",
                      color: "#333",
                      background: "#fff",
                    }}
                  />
                </div>
                {error && (
                  <p style={{ color: "#d32028", fontSize: "0.8rem", margin: "0.35rem 0 0" }}>{error}</p>
                )}
                <p style={{ fontSize: "0.72rem", color: "#aaa", margin: "0.45rem 0 0", lineHeight: 1.55 }}>
                  This email will be used for all future communication. Please provide a valid email address.
                </p>
              </div>

            </div>

            <div style={{ padding: "0rem 1.25rem 1rem", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "0.6rem" }}>
              {loading && <Loader />}
              <button
                onClick={handleContinue}
                disabled={loading}
                style={{
                  width: "55%",
                  padding: "0.8rem 1.5rem",
                  background: loading ? "#aaa" : "#d32028",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: loading ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                Continue
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <button
                  onClick={() => { assignFundSource(userData, sessionStorage.getItem("eligibility_auth_token")); navigate("/eligibility/evaluating", { replace: true, state: { data: userData } }); }}
                  style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                >
                  Skip
                </button>
              )}
            </div>
          </div>
        </div>
        <Footer light />
      </div>
    </div>
  );
}

export default EligibilityCollectEmail;
